import type { GameSummary } from '@brig/api-types';
import { FlagAvatar } from '@brig/theme/avatars';
import { ShipAtAnchor } from '@brig/theme/ornaments';
import { button, paper, visuallyHidden } from '@brig/theme/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMyGames } from '../api/hooks.ts';
import { Cartouche } from '@brig/theme/components';
import { LoadingNotice } from '../components/LoadingNotice.tsx';
import { NewGameButton } from '../components/NewGameButton.tsx';
import { useDocumentTitle } from '../useDocumentTitle.ts';
import * as styles from './Logbook.css.ts';

export function Logbook() {
  const { t } = useTranslation();
  useDocumentTitle(t('logbook.pageTitle'));
  const games = useMyGames();

  return (
    <div className={styles.page}>
      <Cartouche tone="paper" subtitle={t('logbook.lead')}>
        {t('logbook.title')}
      </Cartouche>

      {games.isPending ? <LoadingNotice /> : null}
      {games.isError ? (
        <section className={paper} role="alert">
          <p>{t('profile.loadFailed')}</p>
          <button type="button" className={button({ tone: 'label' })} onClick={() => void games.refetch()}>
            {t('profile.retry')}
          </button>
        </section>
      ) : null}
      {games.data ? (
        games.data.items.length === 0 ? (
          <EmptyLog />
        ) : (
          <GameList games={games.data.items} />
        )
      ) : null}
      <Link to="/how-to-play" className={`${button({ tone: 'label' })} ${styles.learn}`}>
        {t('learn.open')}
      </Link>
    </div>
  );
}

function EmptyLog() {
  const { t } = useTranslation();
  return (
    <section className={`${paper} ${styles.empty}`} aria-labelledby="empty-log-title">
      <ShipAtAnchor className={styles.ship} />
      <h2 id="empty-log-title" className={styles.emptyTitle}>
        {t('logbook.emptyTitle')}
      </h2>
      <p className={styles.emptyBody}>{t('logbook.emptyBody')}</p>
      <NewGameButton />
    </section>
  );
}

function GameList({ games }: { games: GameSummary[] }) {
  const { t, i18n } = useTranslation();
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <section className={paper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t('logbook.columns.opponent')}</th>
            <th scope="col">{t('logbook.columns.status')}</th>
            <th scope="col">{t('logbook.columns.turn')}</th>
            <th scope="col">{t('logbook.columns.updated')}</th>
            <th scope="col">
              <span className={visuallyHidden}>{t('logbook.open')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {games.map((game) => (
            <tr key={game.id}>
              <td>
                <OpponentName game={game} />
              </td>
              <td>{t(`logbook.status.${game.status}`)}</td>
              <td>{game.turn ? t(`logbook.turn.${game.turn}`) : t('logbook.turn.none')}</td>
              <td>
                <time dateTime={game.updatedAt}>{when.format(new Date(game.updatedAt))}</time>
              </td>
              <td>
                <Link to={`/games/${game.id}`}>{t('logbook.open')}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Who a game is against: the opponent with their flag, or how it is played. */
export function OpponentName({ game }: { game: GameSummary }) {
  const { t } = useTranslation();
  return game.opponent ? (
    <span className={styles.opponent}>
      <FlagAvatar id={game.opponent.avatar} size={28} />
      {game.opponent.nickname}
    </span>
  ) : game.mode === 'hotseat' ? (
    <em>{t('logbook.hotseat')}</em>
  ) : game.mode === 'bot' ? (
    <em>{t('logbook.bot')}</em>
  ) : (
    <em>{t('logbook.openInvite')}</em>
  );
}
